import React from 'react';
import { NextSeo } from 'next-seo';

import AppCustomLink from '../components/Common/AppCustomLink';
import { spacing, maxScreenSize } from '../lib/theme';

const appName = 'Meeting Log AI';
const companyName = 'Ted Labs';
const website = 'https://meetinglog.ai';
const supportEmail = 'support@tedlabs.ai';
const lastUpdated = 'October 2, 2026';

export default function TermsAndConditionsPage() {
    return (
        <div className='legal-page'>
            <NextSeo
                title={`Terms and Conditions | ${appName}`}
                description={`The terms that govern your use of ${appName}, the AI voice recorder, transcription and note taking app.`}
                canonical={`${website}/terms-and-conditions`}
            />
            <article className='legal-content'>
                <h1>Terms and Conditions</h1>
                <p className='updated'>Last updated: {lastUpdated}</p>

                <p>Welcome to {appName}. These Terms and Conditions (&quot;Terms&quot;) govern your use of the {appName} website at <a href={website}>{website}</a> and our iOS, Android and macOS apps (together, the &quot;Service&quot;), operated by {companyName} (&quot;we&quot;, &quot;us&quot; or &quot;our&quot;). Please read them carefully.</p>

                <h2>1. Acceptance of Terms</h2>
                <p>By downloading, accessing or using the Service, you agree to be bound by these Terms and by our <AppCustomLink href='/privacy-policy'><a>Privacy Policy</a></AppCustomLink>. If you do not agree, please do not use the Service.</p>

                <h2>2. Description of Service</h2>
                <p>{appName} lets you record audio, upload audio files or link to online media, and uses automated speech recognition and artificial intelligence to generate transcripts, summaries, translations and notes. Features may change, and the Service is provided subject to availability.</p>

                <h2>3. Eligibility and Accounts</h2>
                <p>You must be at least 13 years old (or the minimum age required in your country) to use the Service. You are responsible for keeping your account credentials confidential and for all activity that occurs under your account. Please notify us promptly of any unauthorized use.</p>

                <h2>4. Recording Consent and Acceptable Use</h2>
                <p>Laws on recording conversations differ between countries and states, and some require the consent of every participant. <strong>You are solely responsible for complying with all applicable recording, privacy and wiretapping laws</strong>, including notifying participants and obtaining any required consent before recording.</p>
                <p>You agree not to use the Service to:</p>
                <ul>
                    <li>Record anyone without consent where consent is required by law.</li>
                    <li>Upload or process content you do not have the right to use, or that infringes the intellectual property or privacy rights of others.</li>
                    <li>Process unlawful, harmful, harassing or abusive content.</li>
                    <li>Attempt to reverse engineer, disrupt, overload or gain unauthorized access to the Service or its systems.</li>
                    <li>Resell or commercially redistribute the Service without our written permission.</li>
                </ul>

                <h2>5. Your Content</h2>
                <p>You retain all ownership rights to the audio you record or upload and to the transcripts, summaries and notes generated from it (&quot;Your Content&quot;). You grant us a limited licence to host, store, process and transmit Your Content solely to provide the Service to you, including sending it to our service providers for transcription and AI processing as described in our Privacy Policy. We do not use Your Content to train AI models.</p>
                <p>You can delete Your Content or your account at any time.</p>

                <h2>6. AI-Generated Output</h2>
                <p>Transcripts, summaries and other AI-generated output may contain errors, omissions or inaccuracies. Output is provided for convenience only and should not be relied on as a complete or verbatim record, or as legal, medical, financial or other professional advice. Please review important content before relying on it.</p>

                <h2>7. Subscriptions and Payment</h2>
                <p>Some features require a paid subscription. Prices and plan details are shown in the app before purchase. Subscriptions purchased through the Apple App Store or Google Play are billed to your store account and <strong>renew automatically</strong> at the end of each billing period unless cancelled at least 24 hours before renewal.</p>
                <h3>7.1 Free Trials</h3>
                <p>We may offer a free trial to new users. The trial length is shown at sign-up. Unless you cancel before the trial ends, you will be charged for the subscription plan you selected.</p>
                <h3>7.2 Cancellation</h3>
                <p>You can cancel at any time from your App Store or Google Play subscription settings, or from your account settings if you subscribed on the web. Your subscription stays active until the end of the current billing period, and no further charges are made after cancellation.</p>
                <h3>7.3 Refunds</h3>
                <p>Except where required by law, subscription fees are non-refundable, including for partial billing periods. Refunds for purchases made through the App Store or Google Play are handled by Apple or Google under their refund policies. If you request a refund from Apple, we may share limited information about your use of the app with Apple, solely to help it process your request.</p>

                <h2>8. Intellectual Property</h2>
                <p>Excluding Your Content, the Service and all related software, design, text, graphics and logos are owned by {companyName} or its licensors and are protected by intellectual property laws. You may not copy, modify, distribute or create derivative works from them without our prior written consent.</p>

                <h2>9. Third-Party Services</h2>
                <p>The Service may link to or integrate with third-party services (for example YouTube, the App Store or Google Play). Your use of those services is governed by their own terms, and we are not responsible for them.</p>

                <h2>10. Termination</h2>
                <p>You may stop using the Service and delete your account at any time. We may suspend or terminate your access if you breach these Terms or use the Service in a way that could cause harm or legal liability.</p>

                <h2>11. Disclaimer of Warranties</h2>
                <p>The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind, whether express or implied, including warranties of merchantability, fitness for a particular purpose and non-infringement. We do not guarantee that the Service will be uninterrupted, error-free or that recordings will never be lost, so please keep copies of important content.</p>

                <h2>12. Limitation of Liability</h2>
                <p>To the fullest extent permitted by law, {companyName} will not be liable for any indirect, incidental, special, consequential or punitive damages, or for any loss of data, profits or business, arising from your use of the Service. Our total liability for any claim relating to the Service is limited to the amount you paid us in the 12 months before the claim. Nothing in these Terms limits liability that cannot be limited by law, including for death or personal injury caused by negligence, or fraud.</p>

                <h2>13. Changes to the Service and Terms</h2>
                <p>We may modify or discontinue the Service at any time. We may also update these Terms from time to time and will post the updated version on this page with a new &quot;Last updated&quot; date. Continued use of the Service after changes take effect means you accept the updated Terms.</p>

                <h2>14. Governing Law</h2>
                <p>These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales, without affecting any mandatory consumer protection rights you have in your country of residence.</p>

                <h2>15. Contact Us</h2>
                <p>If you have any questions about these Terms, please contact us:</p>
                <p>
                    {companyName}<br />
                    London, United Kingdom<br />
                    Email: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                </p>
            </article>
            <style jsx>
                {`
                    .legal-page {
                        padding: ${spacing('xxl')} ${spacing('lg')};
                    }
                    .legal-content {
                        max-width: 820px;
                        margin: auto;
                        background: white;
                        padding: ${spacing('xxl')};
                        border-radius: 12px;
                        line-height: 1.7;
                    }
                    .legal-content h2 {
                        margin-top: 2em;
                    }
                    .legal-content h3 {
                        margin-top: 1.25em;
                    }
                    .updated {
                        color: #6b7280;
                    }
                    @media ${maxScreenSize.tablet} {
                        .legal-page {
                            padding: ${spacing('lg')} ${spacing()};
                        }
                        .legal-content {
                            padding: ${spacing('lg')};
                        }
                    }
                `}
            </style>
        </div>
    )
}
